import type { LevelCatalog } from "@highmarch/app";
import type { BalanceData } from "@highmarch/domain";
import { type BusyReason, type GuideApi, type GuideSignal, type HubFacts, guide } from "../guide/guide";
import { PRACTICE_LEVEL, guideUnlocks } from "../guide/topics";
import { showGuideSheet } from "../guide/guideSheet";
import { unlockCardsPending, unlockCardsPlaying } from "../unlockCards";
import { holdsGuideTarget, hubBusy, sheetName } from "./hubGuide";

/** What the hub hands its guide link: where it lives, its facts for a given top sheet, and its own busy states. */
export interface HubGuideHost {
  readonly parent: HTMLElement;
  readonly root: HTMLElement;
  readonly catalog: LevelCatalog;
  /** The hub's facts with `sheet` on top (hubGuide.ts hubFacts). */
  facts(sheet: string | null): HubFacts;
  /** The chest loot flight still counts as busy. */
  lootBusy(): boolean;
  /** Starts a level (Practice). */
  play(level: number): void;
}

/**
 * The hub's side of the guide (Captain Bram, ui/guide): attaches the hub to the installed guide, feeds it the hub's
 * facts on every render, tick and sheet change, tells it when the hub is busy, and opens the Guide sheet (More → Guide).
 */
export class HubGuideLink {
  /** The guide this hub is attached to (a debug reset installs a new one: the next update attaches to it). */
  private guideOn: GuideApi | null = null;
  /** The sheet on top at the last guide update, so a sheet change updates the guide once (not on every mutation). */
  sheet: string | null = null;

  constructor(private readonly h: HubGuideHost) {}

  private get B(): BalanceData { return this.h.catalog.balance; }

  /** The sheet on top of the hub, if any (sheets are the ui root's children; a parked one is hidden). */
  private topSheet(): HTMLElement | null {
    const sheets = [...this.h.parent.children].filter((e): e is HTMLElement => e instanceof HTMLElement && e.classList.contains("sheet") && e.style.visibility !== "hidden");
    return sheets[sheets.length - 1] ?? null;
  }

  busy(): BusyReason | null {
    const top = this.topSheet();
    return hubBusy({
      wipe: document.querySelector(".wipe:not(.hidden)") !== null,
      unlockCards: unlockCardsPlaying() || unlockCardsPending(),
      lootFlying: this.h.lootBusy(),
      dialog: this.h.parent.querySelector(":scope > .scrim") !== null,
      sheet: top ? { name: sheetName([...top.classList]) ?? "other", holdsTarget: holdsGuideTarget(top) } : null,
    });
  }

  /** New facts for the guide (every render, tick and sheet change); attaches the hub to it first. */
  update(): void {
    const g = guide();
    if (!g || !this.h.root.isConnected) return;
    if (this.guideOn !== g) {
      this.guideOn?.detach("hub");
      this.guideOn = g;
      // The overlay mounts on the ui root, above the hub's sheets (the Armoury's Equip best step points into one).
      g.attach("hub", { root: this.h.parent, world: null, busy: () => this.busy(), screen: () => this.h.root });
    }
    const top = this.topSheet();
    this.sheet = top ? sheetName([...top.classList]) : null;
    g.update({ screen: "hub", level: null, end: null, highestCleared: this.h.catalog.highestLevelCleared, signals: new Set(), hub: this.h.facts(this.sheet) });
  }

  signal(s: GuideSignal): void {
    guide()?.signal(s);
    this.update();
  }

  /** More → Guide: the captain's topics, the tips toggle and "Practice battle with me" (level 1, walkthrough on). */
  openSheet(): void {
    showGuideSheet(this.h.parent, {
      highestCleared: this.h.catalog.highestLevelCleared, unlocks: guideUnlocks(this.B),
      onPractice: () => { guide()?.startPractice(); this.h.play(PRACTICE_LEVEL); },
    });
  }

  dispose(): void {
    this.guideOn?.detach("hub");
    this.guideOn = null;
  }
}
